/**
 * Typed client for the Database Context API (knowledge service), proxied by the dashboard at /api/context.
 * Contract: evaluation_platform/services/knowledge_mcp_service/CONTEXT_API.md
 */
import { useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";

export const CONTEXT_API_BASE = "/api/context";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Version {
  version: string;
  updated_at: string;
  live: boolean;
  n_tables: number;
  n_concepts: number;
  note?: string | null;
}

export type TableKind = "fact" | "dim" | "other";

export interface TableQuality {
  tests_passed: number;
  tests_total: number;
}

export interface TableSummary {
  fqn: string;
  domain?: string | null;
  kind: TableKind;
  summary?: string | null;
  quality: TableQuality | null;
}

/** The full card as stored in the bundle. Shape is open: render generically. */
export type TableCard = Record<string, unknown>;

export interface Concept {
  name: string;
  definition?: string | null;
  authoritative_source?: string | null;
  related_tables?: string[];
}

export interface DiffEntry {
  fqn: string;
  field: string;
  before: unknown;
  after: unknown;
}

export type ProposalStatus = "draft" | "checking" | "ready" | "published" | "rejected";

export interface ProposalProof {
  claim: string;
  sql: string;
  result_preview: unknown;
  reproduced: boolean;
}

export interface CheckScore {
  mean: number;
  correct: number;
}

export interface ProposalCheck {
  dataset: string;
  before: CheckScore;
  after: CheckScore;
  n: number;
}

export interface Proposal {
  id: string;
  title: string;
  reason?: string | null;
  created_at: string;
  author?: string | null;
  status: ProposalStatus;
  changes: DiffEntry[];
  proof: ProposalProof[];
  check: ProposalCheck | null;
  base_version?: string | null;
  published_version?: string | null;
}

export type ChatEvent =
  | { type: "status"; text: string }
  | { type: "tool"; name: string; args?: unknown; summary?: string; bytes_billed?: number }
  | { type: "message"; text: string }
  | { type: "proposal"; proposal: Proposal }
  | { type: "done"; cost_usd?: number; bytes_billed?: number }
  | { type: "error"; text: string };

export interface ChatHistory {
  messages: { role: string; text: string; at?: string }[];
  proposals: string[];
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** The context service (or the dashboard proxy route to it) cannot be reached. */
export class ContextUnavailableError extends Error {
  constructor(message = "Contextservice niet bereikbaar") {
    super(message);
    this.name = "ContextUnavailableError";
  }
}

/** The service answered, but with an HTTP error. `detail` is the server's explanation. */
export class ContextApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`);
    this.name = "ContextApiError";
    this.status = status;
    this.detail = detail;
  }
}

export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException ? e.name === "AbortError" : (e as { name?: string })?.name === "AbortError";
}

/** Human readable message for any error thrown by this client. */
export function contextErrorMessage(e: unknown): string {
  if (e instanceof ContextApiError) return e.detail || `Fout ${e.status}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

function extractDetail(body: unknown): string {
  if (!body || typeof body !== "object") return typeof body === "string" ? body : "";
  const b = body as Record<string, unknown>;
  const d = b.detail ?? b.error ?? b.message;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    // FastAPI validation errors: [{loc, msg, type}]
    return d.map((x) => (x && typeof x === "object" && "msg" in x ? String((x as { msg: unknown }).msg) : JSON.stringify(x))).join("; ");
  }
  return d !== undefined ? JSON.stringify(d) : "";
}

const UNAVAILABLE_STATUSES = new Set([502, 503, 504]);

function isHtml(res: Response): boolean {
  return (res.headers.get("content-type") || "").toLowerCase().includes("text/html");
}

/**
 * Throws ContextUnavailableError or ContextApiError for a non-OK response.
 * A 404 counts as "unreachable" only when it is not a meaningful answer from the service itself: non-JSON body,
 * the proxy's "Unknown service" answer, or FastAPI's generic "Not Found" for a route that does not exist.
 * A JSON 404 with another detail (e.g. "table not found") is a normal error.
 */
async function throwForResponse(res: Response): Promise<never> {
  if (UNAVAILABLE_STATUSES.has(res.status) || isHtml(res)) throw new ContextUnavailableError();
  const text = await res.text().catch(() => "");
  let body: unknown = undefined;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  const detail = body !== undefined ? extractDetail(body) : "";
  if (res.status === 404 && (body === undefined || /^unknown service/i.test(detail) || detail === "Not Found" || detail === "")) {
    throw new ContextUnavailableError();
  }
  throw new ContextApiError(res.status, detail || text.slice(0, 300) || res.statusText);
}

type HeaderFn = () => Promise<Record<string, string>>;

function qs(params: Record<string, string | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") sp.set(k, v);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function doFetch(getHeaders: HeaderFn, path: string, init: RequestInit = {}): Promise<Response> {
  const auth = await getHeaders().catch(() => ({}) as Record<string, string>);
  const headers: Record<string, string> = { ...auth, ...(init.headers as Record<string, string> | undefined) };
  if (init.body !== undefined && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${CONTEXT_API_BASE}${path}`, { ...init, headers });
  } catch (e) {
    if (isAbortError(e)) throw e;
    throw new ContextUnavailableError();
  }
  if (!res.ok) await throwForResponse(res);
  return res;
}

async function requestJson<T>(getHeaders: HeaderFn, path: string, init: RequestInit = {}): Promise<T> {
  const res = await doFetch(getHeaders, path, { ...init, headers: { Accept: "application/json", ...(init.headers as Record<string, string> | undefined) } });
  if (isHtml(res)) throw new ContextUnavailableError(); // SPA fallback page instead of the API
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ContextUnavailableError();
  }
}

// ---------------------------------------------------------------------------
// SSE parsing
// ---------------------------------------------------------------------------

function tryParse(s: string): unknown | undefined {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

/**
 * Incremental parser for `text/event-stream`. Feed decoded chunks; it calls `onData` per complete JSON payload.
 * Handles chunk boundaries, \r\n, comments (":"), non-data fields, multi-line `data:` (joined with "\n" per spec)
 * and servers that emit one JSON per `data:` line without blank separators.
 */
export function createSseParser(onData: (payload: unknown) => void) {
  let buffer = "";
  let pending: string[] = [];

  const flush = () => {
    if (!pending.length) return;
    const joined = pending.join("\n");
    pending = [];
    const parsed = tryParse(joined);
    if (parsed !== undefined) {
      onData(parsed);
      return;
    }
    // Fall back: maybe each line was its own JSON object.
    for (const line of joined.split("\n")) {
      const p = tryParse(line);
      if (p !== undefined) onData(p);
    }
  };

  const handleLine = (line: string) => {
    if (line === "") return flush();
    if (line.startsWith(":")) return; // comment / keep-alive
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    if (field !== "data") return; // event:, id:, retry: are not used by this API
    let value = idx === -1 ? "" : line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    // One JSON object per data line without blank separator: dispatch the previous one first.
    if (pending.length && tryParse(pending.join("\n")) !== undefined) flush();
    pending.push(value);
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      const lines = buffer.split(/\r\n|\r|\n/);
      buffer = lines.pop() ?? "";
      for (const l of lines) handleLine(l);
    },
    end() {
      if (buffer) handleLine(buffer);
      buffer = "";
      flush();
    },
  };
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

export function createContextApi(getHeaders: HeaderFn) {
  return {
    listVersions: () => requestJson<Version[]>(getHeaders, "/versions"),

    listTables: (p: { q?: string; domain?: string; version?: string } = {}, signal?: AbortSignal) =>
      requestJson<TableSummary[]>(getHeaders, `/tables${qs(p)}`, { signal }),

    getTable: (fqn: string, version?: string, signal?: AbortSignal) =>
      requestJson<TableCard>(getHeaders, `/tables/${enc(fqn)}${qs({ version })}`, { signal }),

    listConcepts: (version?: string, signal?: AbortSignal) => requestJson<Concept[]>(getHeaders, `/concepts${qs({ version })}`, { signal }),

    diff: (from: string, to: string, signal?: AbortSignal) => requestJson<DiffEntry[]>(getHeaders, `/diff${qs({ from, to })}`, { signal }),

    listProposals: (status?: ProposalStatus, signal?: AbortSignal) =>
      requestJson<Proposal[]>(getHeaders, `/proposals${qs({ status })}`, { signal }),

    getProposal: (id: string, signal?: AbortSignal) => requestJson<Proposal>(getHeaders, `/proposals/${enc(id)}`, { signal }),

    publishProposal: (id: string) =>
      requestJson<{ published_version: string }>(getHeaders, `/proposals/${enc(id)}/publish`, { method: "POST", body: "{}" }),

    rejectProposal: (id: string, reason: string) =>
      requestJson<unknown>(getHeaders, `/proposals/${enc(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),

    rollback: (version: string) => requestJson<unknown>(getHeaders, "/rollback", { method: "POST", body: JSON.stringify({ version }) }),

    getChatHistory: (sessionId: string, signal?: AbortSignal) => requestJson<ChatHistory>(getHeaders, `/chat/${enc(sessionId)}`, { signal }),

    /**
     * POST /chat and stream events. Resolves when the stream ends; rejects with ContextUnavailableError,
     * ContextApiError, or an AbortError when `signal` is aborted.
     */
    async chat(body: { session_id: string; message: string }, onEvent: (e: ChatEvent) => void, signal?: AbortSignal): Promise<void> {
      // The dashboard proxy only streams when Accept is exactly "text/event-stream".
      const res = await doFetch(getHeaders, "/chat", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { Accept: "text/event-stream" },
        signal,
      });
      if (isHtml(res)) throw new ContextUnavailableError();
      const parser = createSseParser((p) => {
        if (p && typeof p === "object" && "type" in p) onEvent(p as ChatEvent);
      });
      if (!res.body) {
        parser.push(await res.text());
        parser.end();
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          parser.push(decoder.decode(value, { stream: true }));
        }
        parser.push(decoder.decode());
        parser.end();
      } finally {
        reader.releaseLock();
      }
    },
  };
}

export type ContextApi = ReturnType<typeof createContextApi>;

/** Stable API instance bound to the current user's auth headers. */
export function useContextApi(): ContextApi {
  const { getAuthHeaders } = useAuth();
  const ref = useRef(getAuthHeaders);
  ref.current = getAuthHeaders;
  return useMemo(() => createContextApi(() => ref.current()), []);
}
